
import { defineComponent, onMounted, ref, SetupContext, computed } from "vue";
import NativeClient from "@/client/native-client";
import {
  PantryFridgeAreaImageData,
  PantryFridgeImageData,
  FRIDGE_ROOM_ID_TYPE,
  FRIDGE_INFO_DOOR_ID_TYPE,
  IMAGE_ORIENTATION_TYPE,
  FRIDGE_AREA_ID_TYPE,
  CameraImageMap,
  FREEZER_AREA_ID_TYPE,
  FridgeCameraRoomInfo
} from "@/types/pantry";
import store from "@/store";
import { convertRemToPx } from "@/utils/common-utils";
import DateUtil from "@/utils/date-util";
import { PantryCameraImage } from "@/types/common";

export default defineComponent({
  name: "PantryImageRoomItem",
  components: {},
  props: {
    data: {
      type: PantryFridgeAreaImageData,
      default: null
    },
    roomId: {
      type: Number,
      defalut: -1
    },
    cameraRoomInfo: {
      type: FridgeCameraRoomInfo,
      default: null,
      required: true
    },
    isSelect: {
      type: Boolean,
      default: false,
      required: true
    },
  },
  setup(props, context: SetupContext) {
    const image = ref<HTMLImageElement>();
    const imageViewWidth = ref(window.innerWidth);
    const imageViewHeight = ref(convertRemToPx(12.8));
    const originalViewWidth = ref(window.innerWidth);
    const originalViewHeight = ref(window.innerHeight - convertRemToPx(8.2));
    const isRequstDraw = ref(false);
    const isShow = ref(false);
    const isNoImage = ref(false);
    const isLoading = ref(true);
    const drawImageData = (
      imageData: string,
      width: number,
      height: number
    ) => {
      if (image.value == undefined) {
        return;
      }
      NativeClient.sendDebugLog("PantryImageRoomItem drawImageData");
      image.value.src = "data:image/jpeg;base64," + imageData;
      image.value.decoding = "async";
      image.value.onload = function() {
        if (image.value == undefined) {
          return;
        }
        NativeClient.sendDebugLog(
          "PantryImageRoomItem drawImageData loaded roomId=" +
            props.data.imageInfo.roomId +
            " areaId=" +
            props.data.imageInfo.areaId +
            " width=" +
            width +
            " height=" +
            height
        );
        const imageWidth = image.value.naturalWidth;
        const imageHeight = image.value.naturalHeight;
        NativeClient.sendDebugLog(
          "PantryImageRoomItem drawImageData loaded roomId=" +
            props.data.imageInfo.roomId +
            " areaId=" +
            props.data.imageInfo.areaId +
            " image.width=" +
            imageHeight +
            " image.height=" +
            imageHeight
        );

        const viewWidth = width;
        const viewHeight = height;
        let scaleRate = viewWidth / imageWidth;
        let scaleWidth = viewWidth;
        let scaleHeight = imageHeight * scaleRate;
        const isScale =
          props.data.imageInfo.orientation == IMAGE_ORIENTATION_TYPE.VERTICAL;
        if (isScale) {
          if (scaleHeight > viewHeight) {
            scaleRate = viewHeight / imageHeight;
            scaleWidth = image.value.width * scaleRate;
            scaleHeight = viewHeight;
          }
        }
        NativeClient.sendDebugLog(
          "PantryImageRoomItem drawImageData loaded roomId=" +
            props.data.imageInfo.roomId +
            " areaId=" +
            props.data.imageInfo.areaId +
            " scaleWidth=" +
            scaleWidth +
            " scaleHeight=" +
            scaleHeight
        );
        imageViewWidth.value = scaleWidth;
        imageViewHeight.value = scaleHeight;
        isShow.value = true;
        isNoImage.value = false;
        isLoading.value = false;
      };
      isRequstDraw.value = true;
    };

    const getHeight = () => {
      const imageHeight = window.innerHeight - convertRemToPx(8.2);
      return imageHeight;
    };
    const getWidth = () => {
      let imageWidth = window.innerWidth;
      switch (props.roomId) {
        case FRIDGE_ROOM_ID_TYPE.DOOR:
          if (props.cameraRoomInfo.reizoDoorTypeId == FRIDGE_INFO_DOOR_ID_TYPE.FRENCH_DOOR) {
            if (props.data.imageInfo.areaId == FRIDGE_AREA_ID_TYPE.LEFT_DOOR) {
              imageWidth = imageWidth * 0.4;
            } else if (props.data.imageInfo.areaId == FRIDGE_AREA_ID_TYPE.RIGHT_DOOR) {
              imageWidth = imageWidth * 0.6;
            }
          }
          break;
        case FRIDGE_ROOM_ID_TYPE.FREEZER:
          if (props.data.imageInfo.areaId == FREEZER_AREA_ID_TYPE.UPPER_LEFT_TRAY ||
             props.data.imageInfo.areaId == FREEZER_AREA_ID_TYPE.UPPER_RIGHT_TRAY ) {
            imageWidth = imageWidth * 0.5;
          }
          break;
      }
      NativeClient.sendDebugLog(
        "PantryImageRoomItem getWidth roomId=" +
          props.data.imageInfo.roomId +
          " areaId=" +
          props.data.imageInfo.areaId +
          " cameraRoomInfo=" +
          JSON.stringify(props.cameraRoomInfo) +
          " imageWidth=" +
          imageWidth
      );
      return imageWidth;
    };
    const setDrawImage = (data: PantryFridgeImageData) => {
      let result = false;
      if (data.info) {
        const map: CameraImageMap = store.getters["pantry/getCameraImageMap"];
        if (map && data.key) {
          NativeClient.sendDebugLog(
            "PantryImageRoomItem setDrawImage getCameraImageMap success"
          );
          let imageData = null;
          const item = map.map.get(data.key);
          NativeClient.sendDebugLog(
            "PantryImageRoomItem setDrawImage key=" + data.key
          );
          if (item && item.imageData) {
            imageData = item.imageData;
            const requst = new PantryCameraImage(data.key, imageData);
            NativeClient.savePantryCameraImage(requst);
          } else {
            NativeClient.sendDebugLog(
              "PantryImageRoomItem setDrawImage no data item=" +
                JSON.stringify(item)
            );
          }
          if (imageData) {
            drawImageData(imageData, originalViewWidth.value, originalViewWidth.value);
            result = true;
          }
        }
      } else {
          NativeClient.sendDebugLog(
            "PantryImageRoomItem setDrawImage failed key=" + data.key
          );
          imageViewWidth.value = getWidth();
          imageViewHeight.value = convertRemToPx(12.8);
          isRequstDraw.value = true;
          isNoImage.value = true;
          isLoading.value = false;
      }
    
      return result;
    };

    const drawScreen = computed(async () => {
      if (props.data != null) {
        if (isRequstDraw.value) {
          return;
        }
        NativeClient.sendDebugLog(
          "PantryImageRoomItem drawScreen props=" +
            JSON.stringify(props)
        );
        if (props.isSelect) {
          const imageData = props.data.getSelectImage();
          if (imageData != null) {
            setDrawImage(imageData);
          }
        }
      }
    });

    const getDate = () => {
      let result = "";
      const imageData = props.data.getSelectImage();
      if (imageData != null && imageData.info && imageData.info.capturedTime != "") {
        const time = imageData.info.capturedTime;
        result = DateUtil.formatYYYYMMDD_hhmm_JST(time);
      }
      return result;
    };

    const clickImage = () => {
      NativeClient.sendDebugLog("PantryImageRoomItem clickImage 1");
      const imageData = props.data.getSelectImage();
      if (imageData != null) {
        NativeClient.sendDebugLog("PantryImageRoomItem clickImage 2");
        context.emit("select-image", imageData.roomId, imageData.areaId, imageData.key);
      }
    };

    const clickDate = () => {
      NativeClient.sendDebugLog("PantryImageRoomItem clickDate 1");
      context.emit("show-date",  props.data.imageInfo.roomId, props.data.imageInfo.areaId);
    }

    const setupDraw = () => {
      if (props.data != null) {
        if (isRequstDraw.value) {
          return;
        }
        const imageWidth = getWidth();
        const imageHeight = getHeight();
        NativeClient.sendDebugLog(
          "[***]PantryImageRoomItem setupDraw roomId=" +
            props.data.imageInfo.roomId +
            " areaId=" +
            props.data.imageInfo.areaId +
            " imageWidth=" +
            imageWidth +
            " imageHeight=" +
            imageHeight
        );
        imageViewWidth.value = getWidth();
        imageViewHeight.value = convertRemToPx(12.8);
        originalViewWidth.value = imageWidth;
        originalViewHeight.value = imageHeight;
      }
    };

    /**
     * コンポーネントがマウントされたときに通知される
     */
    onMounted(() => {
      NativeClient.sendDebugLog("PantryImageRoomItem onMounted");
      setupDraw();
    });

    const imageStyle = computed(() => {
      NativeClient.sendDebugLog(
        "[***]PantryImageRoomItem imageStyle roomId=" +
          props.data.imageInfo.roomId +
          " areaId=" +
          props.data.imageInfo.areaId +
          " imageWidth=" +
          imageViewWidth.value +
          " imageHeight=" +
          imageViewHeight.value
      );
      let marginLeft = "0rem";
      let marginRight = "0rem";
      let marginTop = "0rem";
      let marginBottom = "0rem";
      if (props.data.imageInfo != null) {
        marginTop = props.data.imageInfo.marginTop;
        marginBottom = props.data.imageInfo.marginBottom;
        marginLeft = props.data.imageInfo.marginLeft;
        marginRight = props.data.imageInfo.marginRight;
      }
      return {
        width: imageViewWidth.value + "px",
        height: imageViewHeight.value + "px",
        marginLeft: marginLeft,
        marginRight: marginRight,
        marginTop: marginTop,
        marginBottom: marginBottom
      };
    });

    return {
      image,
      drawScreen,
      getDate,
      clickImage,
      imageStyle,
      isShow,
      clickDate,
      isNoImage,
      isLoading
    };
  }
});
